import "./Footer.css";
import { useLocalContext } from "../../context/LocalContext";

const Footer = () => {
  const { webinfo = {} } = useLocalContext();

  const year = new Date().getFullYear();

  const handleSubscribe = (e) => {
    e.preventDefault();
    const email = e.target.elements.email?.value;
    if (!email) return;
    window.location.href = `mailto:${webinfo.email || "hello@example.com"}?subject=Subscribe&body=Please subscribe ${email} to updates.`;
  };

  const brandName = webinfo.name || "Your Brand";

  return (
    <footer className="ft">
      <div className="ft-wrap">
        {/* Brand + newsletter */}
        <section className="ft-brand">
          <a
            className="ft-logo"
            href="/"
            aria-label={`${brandName} home`}
          >
            <span className="ft-logo-chip" />
            <span className="ft-logo-text">{brandName}</span>
          </a>

          <p className="ft-tagline">
            We help teams connect their website, ads, and CRM into one
            growth system—so every campaign has a clear next step.
          </p>

          <form
            className="ft-news"
            onSubmit={handleSubscribe}
            aria-label="Newsletter signup"
          >
            <label htmlFor="email" className="sr-only">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="you@company.com"
              autoComplete="email"
            />
            <button type="submit" className="btn-primary">
              Get updates
            </button>
          </form>
          <p className="ft-news-note">
            Occasionally, we share playbooks, templates, and ideas—not spam.
          </p>

          <div className="ft-social" aria-label="Social links">
            <a
              href={webinfo.linkedin || "#"}
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
            >
              {/* LinkedIn */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M4.98 3.5A2.5 2.5 0 1 1 2.5 6a2.5 2.5 0 0 1 2.48-2.5zM3 8h4v13H3zM9 8h3.8v1.9h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.08V21H17v-5.6c0-1.33-.02-3.05-1.86-3.05-1.86 0-2.15 1.45-2.15 2.95V21H9z"
                />
              </svg>
            </a>
            <a
              href={webinfo.twitter || "#"}
              target="_blank"
              rel="noreferrer"
              aria-label="Twitter"
            >
              {/* X/Twitter */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M18.244 2H21l-6.52 7.455L22 22h-6.24l-4.88-6.4L5.2 22H2l7.08-8.08L2 2h6.32l4.48 5.92L18.244 2Zm-1.1 18h1.8L7.02 4H5.18l11.964 16Z"
                />
              </svg>
            </a>
            <a
              href={webinfo.instagram || "#"}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
            >
              {/* Instagram */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5a5.5 5.5 0 1 1 0 11.001A5.5 5.5 0 0 1 12 7.5Zm0 2a3.5 3.5 0 1 0 0 7.001 3.5 3.5 0 0 0 0-7.001Zm5.75-2.25a1 1 0 1 1 0 2.001 1 1 0 0 1 0-2.001Z"
                />
              </svg>
            </a>
          </div>
        </section>

        {/* Services */}
        <nav className="ft-col" aria-labelledby="svc-head">
          <h4 id="svc-head">Services</h4>
          <ul>
            <li>
              <a href="/services/go-to-market-strategy">
                Go-to-Market & Funnel Strategy
              </a>
            </li>
            <li>
              <a href="/services/websites-funnels">
                Websites & Landing Funnels
              </a>
            </li>
            <li>
              <a href="/services/paid-growth">
                Paid Growth Engines
              </a>
            </li>
            <li>
              <a href="/services/seo-content">
                SEO & Content Systems
              </a>
            </li>
            <li>
              <a href="/services/automation-crm">
                Automation & CRM Flows
              </a>
            </li>
            <li>
              <a href="/services/growth-partners">
                Ongoing Growth Partnership
              </a>
            </li>
          </ul>
        </nav>

        {/* Explore */}
        <nav className="ft-col" aria-labelledby="exp-head">
          <h4 id="exp-head">Explore</h4>
          <ul>
            <li>
              <a href="/services">What we do</a>
            </li>
            <li>
              <a href="/process">How we work</a>
            </li>
            <li>
              <a href="/our-strategies">Strategy notes</a>
            </li>
            <li>
              <a href="/about">About the team</a>
            </li>
            <li>
              <a href="/contact">Book a call</a>
            </li>
          </ul>
        </nav>

        {/* Contact */}
        <section className="ft-contact" aria-labelledby="cnt-head">
          <h4 id="cnt-head">Contact</h4>
          <address className="ft-addr">
            <div>
              <strong>Email:</strong>{" "}
              <a href={`mailto:${webinfo.email || "hello@example.com"}`}>
                {webinfo.email || "hello@example.com"}
              </a>
            </div>
            <div>
              <strong>Phone:</strong>{" "}
              <a href={`tel:${webinfo.phonecall || ""}`}>
                {webinfo.phone || "+91 80000 00000"}
              </a>
            </div>
            <div>
              <strong>Address:</strong>{" "}
              {webinfo.address || "Operating globally · Remote-first"}
            </div>
            <div className="ft-hours">
              <strong>Hours:</strong>{" "}
              {webinfo.hours || "Mon–Sat · 10:00–19:00 IST"}
            </div>
          </address>

          <div className="ft-badges">
            <span>Strategy-first</span>
            <span>No long-term lock-ins</span>
            <span>Weekly check-ins</span>
          </div>
        </section>
      </div>

      {/* Bottom bar */}
      <div className="ft-bottom">
        <p>
          © {year} {brandName}. All rights reserved.
        </p>
        <nav className="ft-legal" aria-label="Legal">
          <a href="/terms-and-condition">Terms</a>
          <a href="/privacy-policy">Privacy</a>
          <a href="/disclaimer">Disclaimer</a>
        </nav>
      </div>

      {/* Disclaimer */}
      <div className="ft-disclaimer" role="note" aria-label="Site disclaimer">
        The content on this site is general growth and marketing guidance. For
        specific recommendations tailored to your business, please speak directly
        with our team.
      </div>
    </footer>
  );
};

export default Footer;
